import { FC, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import FadeIn from '@/components/FadeIn/FadeIn';
import Image from 'next/image';
import Link from 'next/link';
import { getImageUrl } from '@/lib/cdn';
import {
  HOME_PRODUCT_TABS,
  HOME_TAB_DEMO_VIDEOS,
  ProductTabId,
} from '@/constants/homeProductTabs';
import { BOOK_DEMO_HREF } from '@/constants/publicConversionPaths';
import BookDemoButton from './BookDemoButton';
import DirectAgentsSection from './DirectAgentsSection';
import HomeBrandLogos from './HomeBrandLogos';
import ProductDetailModal from './ProductDetailModal';
import ProductTabsSection from './ProductTabsSection';

const PROFILE_CDN = 'https://assets.marketbetter.ai/profiles';

const HERO_AVATARS = [
  { src: `${PROFILE_CDN}/elizabeth-parks.jpg`, alt: 'Elizabeth Parks' },
  { src: `${PROFILE_CDN}/charlie-evans.jpeg`, alt: 'Charlie Evans' },
  { src: `${PROFILE_CDN}/francis-gallic.jpeg`, alt: 'Francis Gallic' },
];

const HERO = {
  headline: (
    <>
      The ABM platform sales teams <em className='italic'>love</em>.
    </>
  ),
  description:
    'Find target accounts, identify website visitors, and let AI research and reach them, all in one platform.',
  proof: [
    { href: '/case-studies/hologram', value: '$4M+', label: 'new opportunity at Hologram' },
    { href: '/case-studies/levelblue', value: '$1.7M+', label: 'pipeline from 18 new opportunities in one quarter at LevelBlue' },
  ],
};

/** Demo videos stay per audience so existing ?demo= deep links keep working. */
const DEMO_CARDS: Record<ProductTabId, (typeof HOME_PRODUCT_TABS.sales.primaryRow)[number]> = {
  sales: HOME_PRODUCT_TABS.sales.primaryRow[0],
  marketing: HOME_PRODUCT_TABS.marketing.primaryRow[0],
};

const Introduce: FC = () => {
  const posthog = usePostHog();
  const [isHeroDemoOpen, setIsHeroDemoOpen] = useState(false);
  const [demoAudience, setDemoAudience] = useState<ProductTabId>('sales');

  useEffect(() => {
    const openRequestedDemo = (requestedDemo: string | null) => {
      if (requestedDemo !== 'sales' && requestedDemo !== 'marketing') return;

      setDemoAudience(requestedDemo);
      setIsHeroDemoOpen(true);
      posthog?.capture('homepage_demo_deep_link_opened', { audience: requestedDemo });
    };

    const onDemoRequested = (event: Event) => {
      openRequestedDemo((event as CustomEvent<ProductTabId>).detail);
    };

    openRequestedDemo(new URLSearchParams(window.location.search).get('demo'));
    window.addEventListener('marketbetter:open-demo', onDemoRequested);
    return () => window.removeEventListener('marketbetter:open-demo', onDemoRequested);
  }, [posthog]);

  const openHeroDemo = () => {
    setDemoAudience('sales');
    setIsHeroDemoOpen(true);
    posthog?.capture('homepage_hero_cta_clicked', {
      cta: 'watch_demo',
      cta_position: 'hero',
      demo: 'sales',
    });
  };

  return (
    <>
      <section className='section-introduce pt-3 tablet:pt-[50px]'>
        <div
          className='container-fluid px-4 md:px-7.5 mx-auto'
          style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}
        >
          {/* Hero — Figma node 6385:3338 */}
          <div
            role='region'
            aria-label='MarketBetter product overview'
            className='pt-12 pb-20 px-4 sm:py-12 sm:px-10 tablet:py-16 tablet:px-20 bg-white rounded-[20px] relative overflow-hidden'
            style={{
              backgroundImage: `url(${getImageUrl('hero/hero-bg.svg')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <FadeIn variant='down' delay={0.2}>
              <div className='flex flex-col items-center gap-5 sm:gap-6 max-w-[999px] mx-auto text-center'>
                <h1
                  className='font-medium text-black text-center'
                  style={{
                    fontSize: 'clamp(1.375rem, 4vw, 3rem)',
                    lineHeight: 'clamp(1.875rem, 5vw, 4rem)',
                  }}
                >
                  {HERO.headline}
                </h1>

                <p className='max-w-[760px] text-base leading-7 text-slate-600 sm:text-lg sm:leading-8'>
                  {HERO.description}
                </p>

                <div className='flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap'>
                  <button
                    type='button'
                    onClick={openHeroDemo}
                    className='inline-flex h-[50px] min-w-[210px] items-center justify-center gap-2 rounded-[4px] bg-[#0848E7] px-[29px] text-base font-medium text-white transition-all duration-300 hover:bg-[#073dcc]'
                  >
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      aria-hidden
                    >
                      <circle cx='12' cy='12' r='10' />
                      <polygon points='10 8 16 12 10 16 10 8' fill='currentColor' stroke='none' />
                    </svg>
                    Watch 4-minute demo
                  </button>

                  <Link
                    href={BOOK_DEMO_HREF}
                    className='inline-flex h-[50px] items-center justify-center px-3 text-sm font-medium text-[#0848E7] underline-offset-4 hover:underline'
                    onClick={() =>
                      posthog?.capture('homepage_hero_cta_clicked', {
                        cta: 'book_live_demo',
                        cta_position: 'hero',
                      })
                    }
                  >
                    Book a call →
                  </Link>
                </div>

                <div className='grid w-full max-w-[820px] grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white/85 shadow-sm sm:grid-cols-3'>
                  {HERO.proof.map((proof, index) => (
                    <Link
                      key={proof.href}
                      href={proof.href}
                      className={`group px-5 py-4 text-center transition-colors hover:bg-white ${
                        index === 1 ? 'border-y border-slate-200 sm:border-x sm:border-y-0' : ''
                      }`}
                    >
                      <span className='block text-xl font-semibold text-[#0848E7]'>{proof.value}</span>
                      <span className='text-sm text-slate-600 group-hover:text-slate-900'>{proof.label}</span>
                    </Link>
                  ))}
                  <div className='flex items-center justify-center gap-3 px-5 py-4'>
                    <div className='flex items-center' aria-hidden>
                      {HERO_AVATARS.map((avatar, index) => (
                        <div
                          key={avatar.alt}
                          className='relative h-9 w-9 overflow-hidden rounded-full border-2 border-white shadow-sm'
                          style={{
                            marginLeft: index === 0 ? 0 : -16,
                            zIndex: HERO_AVATARS.length - index,
                          }}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={avatar.src}
                            alt=''
                            className='h-full w-full object-cover'
                          />
                        </div>
                      ))}
                    </div>
                    <div className='text-left'>
                      <span className='block text-xl font-semibold text-[#0848E7]'>4.97</span>
                      <span className='text-sm text-slate-600'>rating on G2</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Logo marquee — Figma 6385:3418 — immediately follows hero (~5px gap) */}
          <div className='mt-5 w-full overflow-hidden'>
            <HomeBrandLogos />
          </div>

          {/* Direct agents — prompt mock */}
          <div className='mt-20'>
            <DirectAgentsSection />
          </div>

          {/* Product tabs — Figma 6389:3363, cards from y=1224 */}
          <div className='mt-20'>
            <ProductTabsSection activeTab='sales' />
          </div>

          {/* Blue metrics card — Figma node 6385:4656 */}
          <div className='mt-20'>
            <div className='bg-[#04216B] rounded-xl md:rounded-[20px] p-4 md:p-12 lg:p-14 min-h-[350px] md:min-h-[420px] flex flex-col lg:flex-row gap-8 relative overflow-hidden'>
                <div className='flex-1 lg:w-[60%] relative z-10 flex flex-col justify-between'>
                  <div>
                    <p className='mb-3 text-sm font-medium text-[#79EEC1] md:text-base'>
                      Switching from 6sense, Demandbase or Common Room?
                    </p>
                    <h2
                      className='font-medium text-white mb-8 leading-[1.5]'
                      style={{ fontSize: 'clamp(1.125rem, 4vw, 2.75rem)' }}
                    >
                      Intent, research and outreach.
                      <br />
                      In <span style={{ color: '#3EA67E' }}>one platform</span>.
                    </h2>

                    <div className='flex flex-col md:flex-row gap-[18px] md:gap-8 mb-8'>
                      <div className='flex'>
                        <div
                          className='mr-[10px] self-stretch'
                          style={{ backgroundColor: '#50D5A2', width: '4px' }}
                        />
                        <div>
                          <div
                            className='font-medium text-white'
                            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)', lineHeight: 1.5 }}
                          >
                            Unlimited
                          </div>
                          <div
                            className='text-[#EFEFEF]'
                            style={{ fontSize: 'clamp(0.8125rem, 1.125vw, 1.125rem)', lineHeight: 1.5 }}
                          >
                            Users on every plan, no seat fees
                          </div>
                        </div>
                      </div>
                      <div className='flex'>
                        <div
                          className='mr-[10px] self-stretch'
                          style={{ backgroundColor: '#50D5A2', width: '4px' }}
                        />
                        <div>
                          <div
                            className='font-medium text-white'
                            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)', lineHeight: 1.5 }}
                          >
                            3 channels
                          </div>
                          <div
                            className='text-[#EFEFEF]'
                            style={{ fontSize: 'clamp(0.8125rem, 1.125vw, 1.125rem)', lineHeight: 1.5 }}
                          >
                            Email, LinkedIn and phone from one queue
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='hidden md:block'>
                    <BookDemoButton
                      href={BOOK_DEMO_HREF}
                      label='See it on your accounts'
                      className='!bg-white !text-[#0848E7] hover:!bg-white/90 !border-0 w-fit'
                      onClick={() =>
                        posthog?.capture('homepage_hero_cta_clicked', {
                          cta: 'book_tailored_demo',
                          cta_position: 'metrics_card',
                        })
                      }
                    />
                  </div>
                </div>

                <div className='flex-1 lg:w-[40%] relative flex flex-col items-end justify-center lg:justify-end'>
                  <div className='absolute top-1/2 right-0 transform -translate-y-1/2'>
                    <div
                      className='w-[250px] h-[350px] md:w-[300px] md:h-[400px] rounded-full'
                      style={{ background: '#58EAB2', filter: 'blur(190px)' }}
                    />
                  </div>
                  <div className='relative z-10'>
                  <Image
                    src={getImageUrl('hero/blue-rect-metric.svg')}
                    alt='5x more meetings booked — before vs after comparison'
                    width={400}
                    height={458}
                    className='w-full h-auto max-w-[280px] md:max-w-[360px] lg:max-w-[420px]'
                  />
                  </div>
                </div>
            </div>
          </div>

        </div>
      </section>

      {isHeroDemoOpen && (
        <ProductDetailModal
          card={DEMO_CARDS[demoAudience]}
          tabId={demoAudience}
          demoVideoUrl={HOME_TAB_DEMO_VIDEOS[demoAudience]}
          startInVideoMode
          onClose={() => setIsHeroDemoOpen(false)}
        />
      )}
    </>
  );
};

export default Introduce;
